import {trimValue} from '../utilities/utilities';
import WindowMetrics from '../utilities/window-metrics';
import ScrollLockAnimation from './scroll-lock-animation';

const settings = {
    cssProperties: {
        // Used on 3 elements per hotspot:
        //  Mask hiding the path behind the hotspot image
        //  The hotspot image
        //  Mask hiding the label
        hotspotVisibility: '--hotspot-visibility',
    },

    classNames: {
        overlayContainer: 'comorbidities-illustration__overlay',
        mobileVersion: 'comorbidities-illustration__mobile',
        rightAlignment: 'comorbidities-illustration__hotspot--right',
        belowAlignment: 'comorbidities-illustration__hotspot--below',
    },

    selectors: {
        containers: '.comorbidities-illustration svg',
        defsRoot: ':scope > defs',
        elementRoot: ':scope > g',
        hotspotsContainer: '.comorbidities-illustration__hotspots',
        hotspots: '.comorbidities-illustration__hotspots > g',
        hotspotImage: 'image',
        hotspotLabel: '.comorbidities-illustration__hotspot__label',
        hotspotText: 'text',
        hotspotPath: 'path',
    },

    labelBackground: {
        verticalPadding: {
            mobile: 4,
            desktop: 14,
        },

        horizontalPadding: {
            mobile: 8,
            desktop: 32,
        },
    },

    templates: {
        // Mask that removes the portion of the <path> that extends to
        // the center of the hotspot
        hotspotMaskContainer: `<mask class="comorbidities-illustration__hotspot-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
            </mask>`,
        hotspotMask: `<g>
                <circle />
            </g>`,

        // Mask that reveals labels
        labelMaskContainer: `<mask class="comorbidities-illustration__label-mask">
            </mask>`,

        // Overlay element that draws a hotspot
        overlay: `<g>
                <g class="comorbidities-illustration__image">
                    <image href="/themes/custom/${__themeDirectory}/images/global/comorbidities-hotspot.png" />
                </g>
            </g>`,
    },

    windowTriggerPosition: 0.75,
};

export default class ComorbiditiesIllustration {
    constructor(svg) {
        // Determine what classes are applied to the SVG
        const mobileVersion = svg
            .classList
            .contains(settings.classNames.mobileVersion);

        // Determine the size of the SVG's viewbox and on-screen
        const svgScreenBounds = svg.getBoundingClientRect();
        let svgViewBox = /^(?<x>\d+) (?<y>\d+) (?<width>\d+) (?<height>\d+)$/.exec(svg.getAttribute('viewBox'));
        if (!svgViewBox) {
            return;
        }

        svgViewBox = {
            x: svgViewBox.groups.x,
            y: svgViewBox.groups.y,
            width: svgViewBox.groups.width,
            height: svgViewBox.groups.height,
        };

        // Find hotspots within the SVG
        const hotspotsContainer = svg
            .querySelector(settings.selectors.hotspotsContainer);
        const hotspots = svg
            .querySelectorAll(settings.selectors.hotspots);

        // Create mask & overlay containers
        const defsRoot = svg
            .querySelector(settings.selectors.defsRoot);
        const elementRoot = svg
            .querySelector(settings.selectors.elementRoot);

        let hotspotMaskContainer = createSvgElement('g');
        hotspotMaskContainer.innerHTML = settings
            .templates
            .hotspotMaskContainer;
        hotspotMaskContainer = hotspotMaskContainer.firstChild;
        hotspotMaskContainer.id = Math.round(Math.random() * 10000);
        defsRoot.appendChild(hotspotMaskContainer);
        hotspotsContainer.setAttribute('mask', `url(#${hotspotMaskContainer.id})`);

        const overlayContainer = createSvgElement('g');
        overlayContainer
            .classList
            .add(settings.classNames.overlayContainer);
        elementRoot.appendChild(overlayContainer);

        // Setup hotspots
        const hotspotScrollTriggerElements = [];
        const hotspotVisibility = [];
        const hotspotTimelines = [];
        const hotspotTweens = [];
        for (let i=0; i<hotspots.length; i++) {
            const hotspot = hotspots[i];

            // Create mask for the hotspot, which clips the path
            let hotspotMask = createSvgElement('g');
            hotspotMask.innerHTML = settings.templates.hotspotMask;
            hotspotMask = hotspotMask.firstChild;
            hotspotMask.setAttribute('class', hotspot.getAttribute('class'));
            hotspotMaskContainer.appendChild(hotspotMask);

            // Create overlay associated with this hotspot, which
            // contains the hotspot image layered over the masked
            // region that clips the path
            let overlay = createSvgElement('g');
            overlay.innerHTML = settings.templates.overlay;
            overlay = overlay.firstChild;
            overlay.setAttribute('class', hotspot.getAttribute('class'));
            overlayContainer.appendChild(overlay);

            // Find the label & line
            const hotspotImage = overlay
                .querySelector(settings.selectors.hotspotImage);

            if (!hotspotImage) {
                continue;
            }

            const hotspotLabel = hotspot
                .querySelector(settings.selectors.hotspotLabel);

            if (!hotspotLabel) {
                continue;
            }

            const hotspotText = hotspotLabel
                .querySelector(settings.selectors.hotspotText);

            if (!hotspotText) {
                continue;
            }

            const hotspotPath = hotspot
                .querySelector(settings.selectors.hotspotPath);

            if (!hotspotPath) {
                continue;
            }

            // Determine what classes are applied to the hotspot
            const rightAligned = hotspot
                .classList
                .contains(settings.classNames.rightAlignment);
            const belowAligned = hotspot
                .classList
                .contains(settings.classNames.belowAlignment);

            // Append the endpoint to the path
            const hotspotBounds = hotspot.getBBox();
            const hotspotTextBounds = hotspotText.getBBox();
            const hotspotScreenBounds = hotspot.getBoundingClientRect();
            let pathDefinition = hotspotPath.getAttribute('d');
            const width = trimValue(hotspotTextBounds.width);
            const height = trimValue(hotspotTextBounds.height);
            const horizontalPadding = mobileVersion ?
                settings.labelBackground.horizontalPadding.mobile :
                settings.labelBackground.horizontalPadding.desktop;
            const verticalPadding = mobileVersion ?
                settings.labelBackground.verticalPadding.mobile :
                settings.labelBackground.verticalPadding.desktop;

            let pathEndPoint;
            if (rightAligned) {
                const hotspotXPosition = (
                    // X-distance into SVG based on screen coordinates
                    (
                        (
                            hotspotScreenBounds.x -
                            svgScreenBounds.x
                        ) /
                        svgScreenBounds.width
                    ) *

                    // Base SVG width
                    svgViewBox.width
                );

                pathEndPoint = svgViewBox.width - hotspotXPosition;
            } else {
                pathEndPoint = -hotspotBounds.width;
                pathEndPoint -= horizontalPadding;
            }

            pathDefinition += ` H ${trimValue(pathEndPoint)}`;
            hotspotPath.setAttribute('d', pathDefinition);

            // Find the endpoint of the path
            const pathTotalLength = hotspotPath.getTotalLength();
            const pathEndpoint = hotspotPath.getPointAtLength(pathTotalLength);

            // Create background rect
            const rect = createSvgElement('rect');

            if (rightAligned) {
                rect.setAttribute('x', trimValue(-(width + horizontalPadding)));
            } else {
                rect.setAttribute('x', -horizontalPadding);
            }

            if (belowAligned) {
                rect.setAttribute('y', trimValue(pathEndpoint.y));
            } else {
                rect.setAttribute('y', trimValue(pathEndpoint.y - (height + (verticalPadding * 2))));
            }

            const rectWidth = trimValue(width + (horizontalPadding * 2));
            const rectHeight = trimValue(height + (verticalPadding * 2));

            rect.setAttribute('width', rectWidth);
            rect.setAttribute('height', rectHeight);
            hotspotLabel.insertBefore(rect, hotspotText);

            // Create mask for the label, which is used to execute
            // the reveal animation
            // We're creating a new mask per label - it doesn't render correctly when
            // all the rects are placed in the same mask
            let labelMaskContainer = createSvgElement('g');
            labelMaskContainer.innerHTML = settings
                .templates
                .labelMaskContainer;
            labelMaskContainer = labelMaskContainer.firstChild;
            labelMaskContainer.id = Math.round(Math.random() * 10000);
            defsRoot.appendChild(labelMaskContainer);
            hotspotLabel.setAttribute('mask', `url(#${labelMaskContainer.id})`);

            const labelMask = createSvgElement('rect');
            labelMask.setAttribute('width', rectWidth);
            labelMask.setAttribute('height', rectHeight);
            labelMask.setAttribute('transform-origin', `center ${trimValue(pathEndpoint.y)}`);
            labelMaskContainer.appendChild(labelMask);

            if (rightAligned) {
                labelMask.setAttribute('x', -(width + horizontalPadding));
            } else {
                labelMask.setAttribute('x', -horizontalPadding);
            }

            if (belowAligned) {
                labelMask.setAttribute('y', trimValue(pathEndpoint.y));
            } else {
                labelMask.setAttribute('y', trimValue(pathEndpoint.y - rectHeight));
            }

            // Setup animation
            const progress = {
                hotspot: 0,
                labelMask: 0,
                labelText: 0,
            };

            const timeline = gsap.timeline({
                paused: true,
            });

            timeline.add('start');

            // Hotspot mask & image
            timeline.to(
                progress,
                1.0,
                {
                    ease: 'power2.out',
                    hotspot: 1,
                    onUpdate: () => {
                        const trimmedPercentComplete = trimValue(
                            progress.hotspot,
                        );

                        // Hotspot mask
                        hotspotMask.style.setProperty(
                            settings.cssProperties.hotspotVisibility,
                            trimmedPercentComplete,
                        );

                        // Hotspot image
                        hotspotImage.style.setProperty(
                            settings.cssProperties.hotspotVisibility,
                            trimmedPercentComplete,
                        );
                    },
                },
                0,
            );

            // Path
            timeline.from(
                hotspotPath,
                1.5,
                {
                    ease: 'power2.inOut',
                    drawSVG: 0,
                },
                0,
            );

            // Label mask
            timeline.to(
                progress,
                1.0,
                {
                    ease: 'power2.inOut',
                    labelMask: 1,
                    onUpdate: () => {
                        const trimmedPercentComplete = trimValue(
                            progress.labelMask,
                        );

                        // Label mask
                        labelMask.style.setProperty(
                            settings.cssProperties.hotspotVisibility,
                            trimmedPercentComplete,
                        );
                    },
                },
                1.0,
            );

            // Label text (could use different timings here if we wanted)
            timeline.to(
                progress,
                1.0,
                {
                    ease: 'power2.inOut',
                    labelText: 1,
                    onUpdate: () => {
                        const trimmedPercentComplete = trimValue(
                            progress.labelText,
                        );

                        // Label text
                        hotspotText.style.setProperty(
                            settings.cssProperties.hotspotVisibility,
                            trimmedPercentComplete,
                        );
                    },
                },
                1.0,
            );

            timeline.add('end');

            // Add items to our array for triggering animations
            // Note: the image's position as reported by getBoundingClientRect
            // shifts around as it animates, which is why we're using the rect
            hotspotScrollTriggerElements.push(rect);
            hotspotVisibility.push(false);
            hotspotTimelines.push(timeline);
            hotspotTweens.push(null);

            // // Testing
            // setTimeout(
            //     () => {
            //         timeline.play();
            //     },
            //     500,
            // );
        }

        // Monitor scroll position for triggering and resetting animations
        const scrollCallback = () => {
            // Only continue if we're visible
            if (mobileVersion) {
                if (!WindowMetrics.isMobile()) {
                    return;
                }
            } else {
                if (WindowMetrics.isMobile()) {
                    return;
                }
            }
            const windowTriggerPosition = settings.windowTriggerPosition *
                window.innerHeight;

            // Loop through our hotspots
            hotspotScrollTriggerElements.forEach((triggerElement, index) => {
                const bounds = triggerElement.getBoundingClientRect();
                const visible = hotspotVisibility[index];

                if (bounds.top > windowTriggerPosition) {
                    // Make sure we're not visible
                    if (visible) {
                        // Hide the hotspot
                        hotspotVisibility[index] = false;

                        const timeline = hotspotTimelines[index];
                        const tween = hotspotTweens[index];
                        if (tween) {
                            tween.kill();
                        }

                        hotspotTweens[index] = timeline.tweenTo(
                            'start',
                            {
                                ease: 'power1.out',
                                onComplete: () => {
                                    hotspotTweens[index] = null;
                                },
                            });
                        hotspotTweens[index].timeScale(2);
                    }
                } else {
                    // Make sure we're visible
                    if (!visible) {
                        // Show the hotspot
                        hotspotVisibility[index] = true;

                        const timeline = hotspotTimelines[index];
                        const tween = hotspotTweens[index];
                        if (tween) {
                            tween.kill();
                        }

                        hotspotTweens[index] = timeline.tweenTo(
                            'end',
                            {
                                ease: 'none',
                                onComplete: () => {
                                    hotspotTweens[index] = null;
                                },
                            });
                    }
                }
            });
        };

        ScrollLockAnimation.addScrollPositionChangeCallback(scrollCallback);
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .querySelectorAll(settings.selectors.containers);

        for (let i=0; i<elements.length; i++) {
            new ComorbiditiesIllustration(elements[i]);
        }
    }
}

/*
    Internal functions
*/

function createSvgElement(tagName) {
    return document.createElementNS('http://www.w3.org/2000/svg', tagName);
}
